import { useState } from "react";
import { useTranslation } from "react-i18next";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Web version of FeedbackModal - opens GitHub issues directly
 * System info and email features are not available in web version
 */
export const FeedbackModal = ({ isOpen, onClose }: FeedbackModalProps) => {
  const { t } = useTranslation("components");
  const [feedbackType, setFeedbackType] = useState<string>("bug");
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string>("");

  const feedbackTypes = [
    { value: "bug", label: t("feedback.types.bug") },
    { value: "feature", label: t("feedback.types.feature") },
    { value: "improvement", label: t("feedback.types.improvement") },
    { value: "other", label: t("feedback.types.other") },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedSubject = subject.trim();
    const trimmedBody = body.trim();

    if (!trimmedSubject || !trimmedBody) {
      return;
    }

    // Create GitHub issue URL with pre-filled content
    const issueTitle = encodeURIComponent(`[${feedbackType}] ${trimmedSubject}`);
    const issueBody = encodeURIComponent(trimmedBody);
    const githubUrl = `https://github.com/anthropics/claude-code-history-viewer/issues/new?title=${issueTitle}&body=${issueBody}`;

    window.open(githubUrl, "_blank");

    // Reset form
    setSubject("");
    setBody("");
    onClose();
  };

  const handleOpenGitHub = () => {
    window.open("https://github.com/anthropics/claude-code-history-viewer/issues", "_blank");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t("feedback.title")}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {t("feedback.close")}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Feedback type selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("feedback.type")}
              </label>
              <select
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {feedbackTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("feedback.subjectRequired")}
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("feedback.subjectPlaceholder")}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t("feedback.contentRequired")}
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={
                  feedbackType === "bug"
                    ? t("feedback.placeholders.bug")
                    : feedbackType === "feature"
                    ? t("feedback.placeholders.feature")
                    : t("feedback.placeholders.default")
                }
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                required
              />
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                type="submit"
                disabled={!subject.trim() || !body.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium"
              >
                {t("feedback.openGitHub")}
              </button>

              <button
                type="button"
                onClick={handleOpenGitHub}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                View Issues
              </button>
            </div>
          </form>

          {/* Tips */}
          <div className="mt-6 text-xs text-gray-500 dark:text-gray-400">
            <div className="mb-2">{t("feedback.tips")}</div>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>{t("feedback.tipBugReport")}</li>
              <li>{t("feedback.tipFeatureRequest")}</li>
              <li>{t("feedback.tipScreenshot")}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
